﻿// wwwroot/js/resend-lockout.js
// Reusable Resend/Lockout/Countdown controller
// Namespace-safe via data-resend-namespace. No jQuery. No Bootstrap deps.

(function (global) {
  const defaultOpts = {
    namespace: 'recover', // se usa en keys de storage
    formSelector: 'form', // form que dispara el submit
    buttonSelector: '#btnResendLink',
    timerSelector: '#timerBlock',
    maxShortAttempts: 2, // tras este número pasa a bloqueo largo
    shortLockSeconds: 60, // 1 min
    longLockSeconds: 1800, // 30 min
    waitingText: 'Disponible en {0}', // {0} => "Xm Ys"
    // si quieres ejecutar algo antes del submit (ej: showLoading)
    onBeforeSubmit: null,
  };

  function nsKey(ns, key) {
    return `${ns}_${key}`;
  }

  function parseIntAttr(el, name, fallback) {
    const val = el.dataset[name];
    if (val === null || val === undefined) {
      return fallback;
    }
    const n = parseInt(val, 10);
    return Number.isFinite(n) ? n : fallback;
  }

  function getTextAttr(el, name, fallback) {
    const val = el.dataset[name];
    return val === null || val === undefined || val === '' ? fallback : val;
  }

  function formatTime(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return (m > 0 ? `${m}m ` : '') + `${s} seg`;
  }

  function now() {
    return Date.now();
  }

  function readStoredJson(key) {
    try {
      return JSON.parse(localStorage.getItem(key) || 'null');
    } catch {
      localStorage.removeItem(key);
      return null;
    }
  }

  function initOne(el, opts) {
    const form = el.closest(opts.formSelector) || document.querySelector(opts.formSelector);
    const btn =
      form?.querySelector(opts.buttonSelector) || document.querySelector(opts.buttonSelector);
    const timer =
      form?.querySelector(opts.timerSelector) || document.querySelector(opts.timerSelector);
    if (!form || !btn || !timer) {
      return;
    }

    const ns = opts.namespace;
    const attemptsKey = nsKey(ns, 'resend_attempts');
    const lockKey = nsKey(ns, 'resend_lockout');
    const disabledKey = nsKey(ns, 'buttonDisabled');

    let intervalId = null;

    function getAttempts() {
      return parseInt(localStorage.getItem(attemptsKey) || '0', 10);
    }
    function setAttempts(n) {
      localStorage.setItem(attemptsKey, String(n));
    }

    function enableButton() {
      btn.disabled = false;
      timer.classList.add('d-none');
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      localStorage.setItem(disabledKey, 'false');
    }

    function renderCountdown(endTs) {
      const diff = endTs - now();
      timer.textContent = opts.waitingText.replace('{0}', formatTime(diff));
    }

    function disableButtonFor(ms) {
      btn.disabled = true;
      timer.classList.remove('d-none');
      const endTs = now() + ms;

      // muestra inmediatamente
      renderCountdown(endTs);

      intervalId = setInterval(() => {
        const diff = endTs - now();
        if (diff <= 0) {
          clearInterval(intervalId);
          intervalId = null;
          enableButton();

          localStorage.removeItem(lockKey);
          return;
        }
        renderCountdown(endTs);
      }, 1000);
    }

    function startLock(kind) {
      const seconds = kind === 'long' ? opts.longLockSeconds : opts.shortLockSeconds;
      const end = now() + seconds * 1000;
      localStorage.setItem(lockKey, JSON.stringify({ end, kind }));
      localStorage.setItem(disabledKey, 'true');
      disableButtonFor(seconds * 1000);
    }

    function checkLock() {
      const stored = readStoredJson(lockKey);
      if (!stored) {
        return enableButton();
      }
      const diff = stored.end - now();
      if (diff <= 0) {
        localStorage.removeItem(lockKey);
        enableButton();
      } else {
        disableButtonFor(diff);
      }
    }

    // SUBMIT
    form.addEventListener('submit', (e) => {
      const stored = readStoredJson(lockKey);
      if (stored && stored.end > now()) {
        e.preventDefault();
        return;
      }

      if (typeof opts.onBeforeSubmit === 'function') {
        try {
          opts.onBeforeSubmit();
        } catch (_) {
          /* noop */
        }
      }

      const attempts = getAttempts() + 1;
      setAttempts(attempts);

      const kind = attempts > opts.maxShortAttempts ? 'long' : 'short';
      startLock(kind);

      // si pasó a bloqueo largo reinicia el contador de intentos
      if (kind === 'long') {
        setAttempts(0);
      }
    });

    // Restaurar estado al cargar
    if (localStorage.getItem(disabledKey) === 'true') {
      checkLock();
    } else {
      enableButton();
    }

    return {
      reset: () => {
        enableButton();
        localStorage.removeItem(lockKey);
        localStorage.removeItem(attemptsKey);
        localStorage.removeItem(disabledKey);
      },
      bootstrapShortLockOnce: () => {
        // 1 minuto la primera vez que llegas al segundo formulario
        if (!localStorage.getItem(lockKey)) {
          localStorage.setItem(disabledKey, 'true');
          setAttempts(0);
          startLock('short');
        }
      },
    };
  }

  function readOptionsFrom(el) {
    const o = { ...defaultOpts };
    o.namespace = getTextAttr(el, 'resendNamespace', o.namespace);
    o.formSelector = getTextAttr(el, 'resendForm', o.formSelector);
    o.buttonSelector = getTextAttr(el, 'resendButton', o.buttonSelector);
    o.timerSelector = getTextAttr(el, 'resendTimer', o.timerSelector);
    o.maxShortAttempts = parseIntAttr(el, 'resendMaxShort', o.maxShortAttempts);
    o.shortLockSeconds = parseIntAttr(el, 'resendShort', o.shortLockSeconds);
    o.longLockSeconds = parseIntAttr(el, 'resendLong', o.longLockSeconds);
    o.waitingText = getTextAttr(el, 'resendWaiting', o.waitingText);
    // permitir llamar a window.showLoading si existe
    o.onBeforeSubmit = () => {
      if (typeof window.showLoading === 'function') {
        window.showLoading();
      }
    };
    return o;
  }

  // Modo auto-mount: busca elementos “marcadores” en el DOM
  function mountAll() {
    const markers = document.querySelectorAll('[data-resend-lockout]');
    const controllers = [];
    markers.forEach((marker) => {
      const opts = readOptionsFrom(marker);
      const api = initOne(marker, opts);
      if (api) {
        controllers.push({ el: marker, ns: opts.namespace, api });
      }
    });
    return controllers;
  }

  const ResendLockout = {
    mount: mountAll,
    // helpers de utilidad por namespace
    reset(ns) {
      const attemptsKey = nsKey(ns, 'resend_attempts');
      const lockKey = nsKey(ns, 'resend_lockout');
      const disabledKey = nsKey(ns, 'buttonDisabled');
      localStorage.removeItem(lockKey);
      localStorage.removeItem(attemptsKey);
      localStorage.removeItem(disabledKey);
    },
    // útil cuando el servidor te lleva al 2º formulario por primera vez:
    bootstrapShortLock(ns) {
      const lockKey = nsKey(ns, 'resend_lockout');
      if (!localStorage.getItem(lockKey)) {
        const end = now() + 60 * 1000;
        localStorage.setItem(lockKey, JSON.stringify({ end, kind: 'short' }));
        localStorage.setItem(nsKey(ns, 'buttonDisabled'), 'true');
      }
    },
  };

  global.ResendLockout = ResendLockout;

  document.addEventListener('DOMContentLoaded', () => {
    // auto-mount por defecto
    ResendLockout.mount();
  });
})(window);
